import type { AstroGlobal } from "astro";
import retry from "fetch-retry";
import isEmpty from "lodash.isempty";

const fetch = retry(globalThis.fetch);

export enum ProductPath {
  PRO_MONTHLY = "videoscribe-monthly",
  PRO_ANNUAL = "videoscribe-yearly",
  PRO_MONTHLY_REGION1 = "videoscribe-monthly-region1",
  PRO_ANNUAL_REGION1 = "videoscribe-yearly-region1",
  PRO_TEAM = "videoscribe-annual-multi-user-b2c",
  PRO_MONTHLY_REGION1_MANUAL = "videoscribe-monthly-region1-manual",

  PLUS_MONTHLY = "videoscribe-plus-monthly",
  PLUS_ANNUAL = "videoscribe-plus-annual",
  PLUS_MONTHLY_REGION1 = "videoscribe-plus-monthly-region1",
  PLUS_ANNUAL_REGION1 = "videoscribe-plus-annual-region1",
  PLUS_MONTHLY_REGION1_MANUAL = "videoscribe-plus-monthly-region1-manual",

  LITE_MONTHLY = "videoscribe-lite-monthly",
  LITE_ANNUAL = "videoscribe-lite-annual",
  LITE_MONTHLY_REGION1 = "videoscribe-lite-monthly-region1",
  LITE_ANNUAL_REGION1 = "videoscribe-lite-annual-region1",
}

export const YEARLY_PRODUCTS = [
  ProductPath.PRO_ANNUAL,
  ProductPath.PRO_ANNUAL_REGION1,
  ProductPath.PLUS_ANNUAL,
  ProductPath.PLUS_ANNUAL_REGION1,
  ProductPath.LITE_ANNUAL,
  ProductPath.LITE_ANNUAL_REGION1,
];

export const MONTHLY_PRODUCTS = [
  ProductPath.PRO_MONTHLY,
  ProductPath.PRO_MONTHLY_REGION1,
  ProductPath.PRO_MONTHLY_REGION1_MANUAL,
  ProductPath.PLUS_MONTHLY,
  ProductPath.PLUS_MONTHLY_REGION1,
  ProductPath.PLUS_MONTHLY_REGION1_MANUAL,
  ProductPath.LITE_MONTHLY,
  ProductPath.LITE_MONTHLY_REGION1,
];

export const TEAM_PRODUCTS = [ProductPath.PRO_TEAM];

export const ALLOWED_PATHS = [
  ...YEARLY_PRODUCTS,
  ...MONTHLY_PRODUCTS,
  ...TEAM_PRODUCTS,
];

const fastspringApiHost = `https://api.fastspring.com`;

const getFastspringAuth = () => {
  const username = import.meta.env.FASTSPRING_API_USERNAME;
  const password = import.meta.env.FASTSPRING_API_PASSWORD;
  const auth = Buffer.from(username + ":" + password).toString("base64");

  return {
    Authorization: `Basic ${auth}`,
  };
};

const defaultRetryOptions = {
  retries: 3,
  retryDelay: 2000,
};

export const getProducts = async () => {
  const auth = getFastspringAuth();

  try {
    const productsResponse = await fetch(`${fastspringApiHost}/products/`, {
      headers: {
        ...auth,
      },
      ...defaultRetryOptions,
    });

    if (!productsResponse.ok) {
      throw Error(productsResponse.statusText);
    }

    const products = await productsResponse.json();

    const productsQS = products.products.join(",");

    const productsDetailResponse = await fetch(
      `${fastspringApiHost}/products/${productsQS}`,
      {
        headers: {
          ...auth,
        },
      },
    );

    if (!productsDetailResponse.ok) {
      throw Error(productsDetailResponse.statusText);
    }

    let { products: productsDetails } = await productsDetailResponse.json();
    productsDetails = productsDetails.filter(
      (product: any) =>
        product &&
        product.attributes &&
        product.attributes.website === "videoscribe",
    );
    return productsDetails;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export interface ProductAndPricing {
  product: ProductPath;
  pricing: {
    [countryCode: string]: PriceInfo;
  };
}
interface PricesResponse {
  products: ProductAndPricing[];
}

export const getAllPricesByCountryCurrency = async (
  countryCode: string,
  currency: string,
): Promise<PricesResponse> => {
  const auth = getFastspringAuth();
  try {
    const productPrices = await fetch(
      `${fastspringApiHost}/products/price?country=${countryCode}&currency=${currency}`,
      {
        headers: {
          ...auth,
        },
        ...defaultRetryOptions,
      },
    );

    if (!productPrices.ok) {
      throw Error(productPrices.statusText);
    }

    const priceData = await productPrices.json();
    return priceData;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const getProductPriceInCountryCurrency = async (
  productId: string,
  countryCode: string,
  currency: string,
): Promise<PriceInfo> => {
  const auth = getFastspringAuth();
  try {
    const productPrices = await fetch(
      `${fastspringApiHost}/products/price/${productId}?country=${countryCode}&currency=${currency}`,
      {
        headers: {
          ...auth,
        },
        ...defaultRetryOptions,
      },
    );

    if (!productPrices.ok) {
      throw Error(productPrices.statusText);
    }

    const priceData = await productPrices.json();
    return priceData.products[0]?.pricing[countryCode];
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const orderVideoscribeProducts = (products: { path: string }[]) => {
  const productOrder = [/monthly/, /yearly/, /quarterly/, /oneoff/];

  products = products.sort((a, b) => {
    let loc1 = productOrder.find((rx) => rx.test(a.path));
    let loc2 = productOrder.find((rx) => rx.test(b.path));

    if (!loc1 || !loc2) return 0;

    return productOrder.indexOf(loc1) - productOrder.indexOf(loc2);
  });

  return products;
};

export const getProductData = (productData: {
  groups: [{ items: { path: ProductPath }[] }];
}) => {
  const items = productData.groups[0].items;
  const allowedItems = items.filter((item) =>
    ALLOWED_PATHS.includes(item.path),
  );
  const orderedProductData = orderVideoscribeProducts(allowedItems);
  return orderedProductData.slice(0, 2);
};

export interface PriceInfo {
  price: number;
  currency: string;
  display: string;
  quantityDiscount: {
    [key: string]: {
      unitPrice: number;
    };
  };
}
export const MAX_TEAM_LICENSES = 60;

export const calculateVolumeAndUnitPrice = (
  priceInfo: PriceInfo,
  requiredLicences: number,
) => {
  let unitPrice = priceInfo.price;
  if (!isEmpty(priceInfo.quantityDiscount)) {
    for (const volume in priceInfo.quantityDiscount) {
      const volumeNumber = Number(volume);
      if (requiredLicences >= volumeNumber) {
        unitPrice = priceInfo.quantityDiscount[volume].unitPrice;
      } else {
        break;
      }
    }
  }

  const volumePrice = unitPrice * requiredLicences;

  return { unitPrice, volumePrice };
};

export const getStorefront = (countryCode: string, Astro: AstroGlobal) => {
  return import.meta.env.PUBLIC_FASTSPRING_STOREFRONT;
};
